import React from 'react';
import { View, Text, StyleSheet, ViewStyle, TextStyle } from 'react-native';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';

interface StepHeaderProps {
  title: string;
  subtitle?: string;
}

/**
 * StepHeader - Header component for wizard steps
 */
export const StepHeader: React.FC<StepHeaderProps> = ({ title, subtitle }) => (
  <View style={styles.headerContainer}>
    <Text style={styles.headerTitle}>{title}</Text>
    {subtitle && <Text style={styles.headerSubtitle}>{subtitle}</Text>}
  </View>
);

/**
 * Shared styles for maintenance wizard steps
 */
export const maintenanceStepStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    paddingHorizontal: 24,
    paddingVertical: 0,
    gap: 12,
  },
  
  // Selection items (radio options)
  selectionItem: {
    backgroundColor: MARKETPLACE_COLORS.uploadAreaBackground,
    borderRadius: 8,
    paddingHorizontal: 20,
    paddingVertical: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  selectionItemExpanded: {
    backgroundColor: MARKETPLACE_COLORS.uploadAreaBackground,
    borderRadius: 8,
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 20,
    gap: 16,
  },
  selectionItemText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 21,
  },

  // Category cards (with icon and description)
  categoryCard: {
    backgroundColor: MARKETPLACE_COLORS.uploadAreaBackground,
    borderRadius: 8,
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 20,
    flexDirection: 'row',
    gap: 16,
  },
  categoryIconContainer: {
    width: 46,
    height: 46,
    borderRadius: 23,
    backgroundColor: MARKETPLACE_COLORS.white10Percent,
    alignItems: 'center',
    justifyContent: 'center',
  },
  categoryContentContainer: {
    flex: 1,
    gap: 16,
  },
  categoryHeaderRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  categoryTitle: {
    flex: 1,
    fontSize: 14,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 21,
  },
  categoryDescription: {
    fontSize: 12,
    fontWeight: '300',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 18,
  },

  // Radio buttons
  radioContainer: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioOuter: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: MARKETPLACE_COLORS.inputBorder,
    backgroundColor: MARKETPLACE_COLORS.inputBackground,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioOuterSelected: {
    backgroundColor: MARKETPLACE_COLORS.accentRed,
    borderColor: MARKETPLACE_COLORS.accentRed,
  },
  radioInner: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: MARKETPLACE_COLORS.textPrimary,
  },

  // Toggle bar
  toggleBar: {
    backgroundColor: MARKETPLACE_COLORS.uploadAreaBackground,
    borderRadius: 8,
    padding: 2,
    flexDirection: 'row',
  },
  toggleOption: {
    flex: 1,
    height: 30,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  toggleOptionActive: {
    backgroundColor: MARKETPLACE_COLORS.inputBackground,
  },
  toggleOptionText: {
    fontSize: 12,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 18,
  },
  toggleOptionTextActive: {
    color: MARKETPLACE_COLORS.textPrimary,
  },

  // Input fields
  inputContainer: {
    backgroundColor: MARKETPLACE_COLORS.inputBackground,
    borderWidth: 1,
    borderColor: MARKETPLACE_COLORS.inputBorder,
    borderRadius: 8,
    paddingHorizontal: 16,
  },
  inputLabel: {
    fontSize: 10,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 12.5,
    marginTop: 4,
  },
  inputText: {
    fontSize: 14,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 17.5,
    paddingVertical: 4,
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
    paddingTop: 11,
    paddingBottom: 11,
  },

  // Dropdown/Select
  selectContainer: {
    backgroundColor: MARKETPLACE_COLORS.white5Percent,
    borderRadius: 8,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    paddingHorizontal: 12,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottomWidth: 1,
    borderBottomColor: MARKETPLACE_COLORS.textPrimary,
  },
  selectText: {
    flex: 1,
    fontSize: 16,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 24,
  },
  selectTextFilled: {
    color: MARKETPLACE_COLORS.textPrimary,
  },

  // Alert box
  alertBox: {
    backgroundColor: 'rgba(255, 118, 41, 0.1)',
    borderRadius: 8,
    padding: 16,
    flexDirection: 'row',
    gap: 10,
  },
  alertText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '400',
    color: '#FF7629',
    lineHeight: 21,
  },

  // Phone number row
  phoneRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  phoneIconContainer: {
    width: 20,
    height: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  phoneNumber: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    color: MARKETPLACE_COLORS.accentRed,
    lineHeight: 21,
  },

  // Date/time field
  dateTimeField: {
    backgroundColor: MARKETPLACE_COLORS.uploadAreaBackground,
    borderRadius: 8,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: MARKETPLACE_COLORS.textPrimary,
  },
  dateTimeLabel: {
    fontSize: 12,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 12,
    letterSpacing: 0.15,
  },
  dateTimeValue: {
    fontSize: 16,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 24,
  },
  dateTimePlaceholder: {
    color: MARKETPLACE_COLORS.textSecondary,
  },
});

const styles = StyleSheet.create({
  headerContainer: {
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 20,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 30,
  },
  headerSubtitle: {
    fontSize: 14,
    fontWeight: '400',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 21,
    marginTop: 4,
  },
});

/**
 * RadioButton component
 */
interface RadioButtonProps {
  selected: boolean;
}

export const RadioButton: React.FC<RadioButtonProps> = ({ selected }) => (
  <View style={maintenanceStepStyles.radioContainer}>
    <View style={[
      maintenanceStepStyles.radioOuter,
      selected && maintenanceStepStyles.radioOuterSelected,
    ]}>
      {selected && <View style={maintenanceStepStyles.radioInner} />}
    </View>
  </View>
);

/**
 * ToggleBar component for Yes/No selection
 */
interface ToggleBarProps {
  options: string[];
  selectedIndex: number;
  onSelect: (index: number) => void;
}

export const ToggleBar: React.FC<ToggleBarProps> = ({ options, selectedIndex, onSelect }) => (
  <View style={maintenanceStepStyles.toggleBar}>
    {options.map((option, index) => (
      <View
        key={option}
        style={[
          maintenanceStepStyles.toggleOption,
          selectedIndex === index && maintenanceStepStyles.toggleOptionActive,
        ]}
        onTouchEnd={() => onSelect(index)}
      >
        <Text style={[
          maintenanceStepStyles.toggleOptionText,
          selectedIndex === index && maintenanceStepStyles.toggleOptionTextActive,
        ]}>
          {option}
        </Text>
      </View>
    ))}
  </View>
);
